import { useState, useEffect, useRef, memo } from "react";
import { useContext, useMemo } from "react";
import LevelTransmitterIndicator from "../components/LevelTransmitterIndicator";
import Transmitter from "../components/Transmitter";
import ControlValveControlPanel from "../components/ControlValveControlPanel";
import Valve from "../components/Valve";
import "./PreHeatTrain1.css";
import { WellContext } from "../context/WellContext";
import { AlarmContext } from "../context/AlarmContext";
import { PumpContext } from "../context/PumpContext";
import { ProcessContext } from "../context/ProcessContext";
import Pump from "../components/Pump";
import PumpFaceplate from "../components/PumpFaceplate";
import MDSFaceplate from "../components/MDSFaceplate";
import { PlantStore } from "../engine/PlantStore";
import { usePlantStore } from "../engine/usePlantStore";
import { trackPageVisit } from "../competency/PageTracker";
import TransmitterFaceplate from "../components/TransmitterFaceplate";





function ValveOutput({
    tag,
    value,
    x,
    y,
    r = 0,
    description = "Control Valve Opening"
}) {

    const [hover, setHover] = useState(false);
    const [showFaceplate, setShowFaceplate] = useState(false);

    return (
        <>
            {/* Hover */}
            {hover && (
                <div
                    style={{
                        position: "absolute",
                        left: x + 30,
                        top: y - 5,
                        background: "rgba(0,80,180,.85)",
                        color: "#fff",
                        padding: "5px 8px",
                        borderRadius: "4px",
                        border: "1px solid #4fc3ff",
                        fontSize: "11px",
                        zIndex: 9999,
                        pointerEvents: "none",
                        whiteSpace: "nowrap"
                    }}
                >
                    <strong>{tag}</strong><br />
                    {description}
                </div>
            )}

            {/* Original Valve Output */}
            <div
                onMouseEnter={() => setHover(true)}
                onMouseLeave={() => setHover(false)}
                onClick={() => setShowFaceplate(true)}
                style={{
                    position: "absolute",
                    left: x,
                    top: y,

                    color: "lime",
                    background: "black",

                    fontSize: "10px",
                    padding: "1px 4px",

                    borderRadius: "4px",
                    border: "1px solid #555",

                    transform: `rotate(${r}deg)`,
                    transformOrigin: "center",

                    cursor: "pointer",

                    fontWeight: "bold",

                    minWidth: "22px",
                    textAlign: "center",

                    transition: "0.2s",
                    boxShadow: hover
                        ? "0 0 8px rgba(0,255,255,.7)"
                        : "none"
                }}
            >
                {Number(value).toFixed(1)} %
            </div>

            {showFaceplate && (
                <TransmitterFaceplate
                    tag={tag}
                    value={Number(value)}
                    unit="%"
                    min={0}
                    max={100}
                    HH={100}
                    H={90}
                    L={10}
                    LL={0}
                    description={description}
                    x={x + 35}
                    y={y + 20}
                    onClose={() => setShowFaceplate(false)}
                />
            )}
        </>
    );
}




function PreHeatTrain1() {
  // ✅ Use ref to track render count
  const renderCount = useRef(0);
  renderCount.current += 1;
  console.log(`PreHeatTrain1 Render: ${renderCount.current}`);

  // ✅ Track page visit only once
  useEffect(() => {
    trackPageVisit("PreHeatTrain1");
  }, []);

  const {processData,  setProcessData} = useContext(ProcessContext);
  const { toggleValve, valves } = useContext(WellContext);
  const [hoveredValve, setHoveredValve] = useState(null);

  const DEFAULT_VALVES = {
    FCV_1901: { mode: "Manual", opening: 0, sp: "", pv: "" },
  };

  const [valveData, setValveData] = useState(() => {
    const saved = localStorage.getItem("PreHeatTrain1_valveData");
    if (!saved) return DEFAULT_VALVES;
    try {
      const parsed = JSON.parse(saved);
      return {
        FCV_1901: { ...DEFAULT_VALVES.FCV_1901, ...parsed.FCV_1901 },
      };
    } catch {
      return DEFAULT_VALVES;
    }
  });

  /* ✅ Persist */
  useEffect(() => {
    localStorage.setItem("PreHeatTrain1_valveData", JSON.stringify(valveData));
  }, [valveData]);

  const updateValveData = (tag, field, value) => {
    setValveData(prev => ({
      ...prev,
      [tag]: {
        ...(prev[tag] || DEFAULT_VALVES[tag]),
        [field]: field === "opening" ? Number(value) || 0 : value
      }
    }));
  };

  /* ================= PANEL CONTROL ================= */
  const [openPanels, setOpenPanels] = useState([]);
  const openPanel = (tag) => {
    if (!openPanels.includes(tag)) {
      setOpenPanels(prev => [...prev, tag]);
    }
  };
  const closePanel = (tag) => {
    setOpenPanels(prev => prev.filter(p => p !== tag));
  };

  // ================= PUMP PANEL LOGIC =================
  const [openPumpPanels, setOpenPumpPanels] = useState([]);
  const openPumpPanel = (tag) => {
    if (!openPumpPanels.includes(tag)) {
      setOpenPumpPanels(prev => [...prev, tag]);
    }
  };
  const closePumpPanel = (tag) => {
    setOpenPumpPanels(prev => prev.filter(p => p !== tag));
  };

  const tagStyle = {
    position: "absolute",
    top: -30,
    left: "50%",
    transform: "translateX(-50%)",
    background: "black",
    color: "#00ffcc",
    fontSize: "13px",
    fontWeight: "bold",
    padding: "3px 8px",
    borderRadius: "4px",
    border: "1px solid #555",
    whiteSpace: "nowrap",
    zIndex: 999
  };

  // ================= RETREIVING OF DATA FROM OTHER PAGES VIA LOCAL STROAGE =================
  const { transmitters, flowOverrides } = usePlantStore();
  const P101_ControlValve = transmitters.P101_ControlValve || 0;
  const TT_14007 = transmitters.TT_14007 || 0;
  const FIC_1506 = transmitters.FIC_1506 || 0;
  const FIC_24013 = transmitters.FIC_24013 || 0;
  const PT_24013 = transmitters.PT_24013 || 0;

  const xv1901Open = !valves?.XV_1901;
  const xv1902Open = !valves?.XV_1902;
  const xv1903Open = !valves?.XV_1903;
  const xv1904Open = !valves?.XV_1904;
  const xv1905Open = !valves?.XV_1905;
  const xv1906Open = !valves?.XV_1906;
  const xv1907Open = !valves?.XV_1907;
  const xv1908Open = !valves?.XV_1908;
  const xv1909Open = !valves?.XV_1909;
  const xv19010Open = !valves?.XV_19010;

  /* =========================================================
     PUMP CONTEXT
  ========================================================= */
  const pumpContext = useContext(PumpContext) || {};
  const pumpData = pumpContext.pumpData || {};

  /* =========================================================
     P-101A / B STATUS
  ========================================================= */
  const P101A_Running = pumpData?.["P-101A"]?.running ?? false;
  const P101B_Running = pumpData?.["P-101B"]?.running ?? false;
  const P101_Running = P101A_Running || P101B_Running;

  /* =========================================================
     SUCTION FLOW FIC-1506
  ========================================================= */
  const suctionFlow1506 = Number(FIC_1506 || 0);
  const suctionAvailable = suctionFlow1506 > 0;

  /* =========================================================
     PUMP CAPACITY
  ========================================================= */
  const P101_CAPACITY = 34.893;

  /* =========================================================
     FCV-1901 OPENING
  ========================================================= */
  const FCV1901_Opening = P101_ControlValve;

  /* =========================================================
     PUMP FLOW TARGET
  ========================================================= */
  const PumpAvailableFlow = (P101_Running && suctionAvailable) ? P101_CAPACITY : 0;

  /* =========================================================
   FIC-1901 DISCHARGE FLOW
========================================================= */

const FIC_1901 = flowOverrides?.FIC_1505 ? 0 : (processData.FIC_1901 ?? 0);

const targetFIC_1901 =
  PumpAvailableFlow * FCV1901_Opening;

useEffect(() => {

  const lastUpdate =
    Number(
      localStorage.getItem(
        "FIC_1901_LastUpdate"
      )
    ) || Date.now();

  const elapsedSeconds =
    (Date.now() - lastUpdate) / 1000;

  if (elapsedSeconds <= 1) return;

  setProcessData(prev => {

    const current =
      Number(prev.FIC_1901 || 0);


    const systemActive =
      P101A_Running ||
      P101B_Running;

    const target =
      systemActive
        ? targetFIC_1901
        : 0;

    /* ====================================
       MAXIMUM 100 SCANS
       ==================================== */

    const scans =
      Math.min(
        Math.floor(
          elapsedSeconds * 10
        ),
        100
      );

    const factor =
      Math.pow(
        1 - 0.12,
        scans
      );

    const next =
      target -
      (
        target - current
      ) * factor;

    return {
      ...prev,
      FIC_1901: Number(
        next.toFixed(3)
      )
    };

  });

}, [
  targetFIC_1901,
  P101A_Running,
  P101B_Running
]);



useEffect(() => {

  localStorage.setItem(
    "FIC_1901_LastUpdate",
    String(Date.now())
  );

}, [FIC_1901]);






/* =========================================================
   REFS
========================================================= */

const targetFIC_1901_Ref =
  useRef(targetFIC_1901);

const intervalFIC_1901_Ref =
  useRef(null);

const isFIC1901Mounted =
  useRef(false);

/* =========================================================
   KEEP TARGET UPDATED
========================================================= */

useEffect(() => {

  targetFIC_1901_Ref.current =
    targetFIC_1901;

}, [targetFIC_1901]);

/* =========================================================
   FIC-1901 FLOW SIMULATION
========================================================= */

useEffect(() => {

  if (isFIC1901Mounted.current) {
    console.log(
      "⚠️ FIC_1901 interval already exists"
    );
    return;
  }

  isFIC1901Mounted.current = true;

  console.log(
    "✅ FIC_1901 interval created"
  );

  intervalFIC_1901_Ref.current =
    setInterval(() => {

      setProcessData(prev => {

        const currentFlow =
          Number(prev.FIC_1901 ?? 0);

        const next =
          currentFlow +
          (
            targetFIC_1901_Ref.current -
            currentFlow
          ) * 0.12;

        return {
          ...prev,

          FIC_1901: Number(
            next.toFixed(3)
          )
        };

      });

    }, 500);

  return () => {

    console.log(
      "❌ FIC_1901 interval cleared"
    );

    if (
      intervalFIC_1901_Ref.current
    ) {

      clearInterval(
        intervalFIC_1901_Ref.current
      );

      intervalFIC_1901_Ref.current =
        null;
    }

    isFIC1901Mounted.current =
      false;

  };

}, []);

  /* =========================================================
     PT-1901 DISCHARGE PRESSURE
  ========================================================= */
  let targetPT_1901 = 0.03;
  if (P101_Running && suctionAvailable) {
    targetPT_1901 = 0.03 + (2.9 - 0.03) * FCV1901_Opening;
  }
  const PT_1901 = Number(targetPT_1901.toFixed(3));

  /* =========================================================
     TT-1901 DISCHARGE TEMPERATURE
  ========================================================= */
  let targetTT_1901 = 25;
  if (P101_Running && suctionAvailable) {
    targetTT_1901 = 37.78;
    if (FCV1901_Opening <= 0.35 && FCV1901_Opening >= 0.03) {
      const restrictionFactor = (0.35 - FCV1901_Opening) / 0.32;
      targetTT_1901 = 37.78 + (restrictionFactor * 3);
    }
  }
  const TT_1901 = Number(targetTT_1901.toFixed(2));










/* =========================================================
   SPLITTING CRUDE MAIN LINE TO E-101A and E-101B
========================================================= */
const openBranchE101 = [xv1901Open, xv1902Open].filter(Boolean).length;

const FIC_1902 =
  xv1901Open && openBranchE101 > 0
    ? FIC_1901 / openBranchE101
    : 0;

const FIC_1903 =
  xv1902Open && openBranchE101 > 0
    ? FIC_1901 / openBranchE101
    : 0;

const FIC_1904 = FIC_1902 + FIC_1903;

/* =========================================================
   HOT SIDE INLET FROM T-102
========================================================= */
const TT_1902 = xv1903Open ? 126 : 25;

/* =========================================================
   PROCESS TAGS FROM CONTEXT
========================================================= */
const TT_1903 = processData.TT_1903 ?? 38;
const TT_1904 = processData.TT_1904 ?? 38;
const TT_1905 = processData.TT_1905 ?? 126;
const TT_1906 = processData.TT_1906 ?? 126;
const TT_1907 = processData.TT_1907 ?? 126;
const TT_19010 = processData.TT_19010 ?? 38;

const TT_19011 = xv1906Open ? TT_14007 : 0; // TT_14007 (ADD THIS LATER)

/* =========================================================
   REFS
========================================================= */
const E101_intervalRef = useRef(null);
const E101_isMounted = useRef(false);

const TT_1903_Ref = useRef(TT_1903);
const TT_1904_Ref = useRef(TT_1904);
const TT_1905_Ref = useRef(TT_1905);
const TT_1906_Ref = useRef(TT_1906);
const TT_1907_Ref = useRef(TT_1907);
const TT_19010_Ref = useRef(TT_19010);

const processRef = useRef({});

/* =========================================================
   LIVE PROCESS VALUES
========================================================= */
useEffect(() => {
  processRef.current = {
    TT_1901,
    TT_1902,
    xv1901Open,
    xv1902Open,
    xv1903Open,
    xv1907Open,
    xv1908Open,
    P101_Running,
    FIC_1902,
    FIC_1903
  };
});

/* =========================================================
   KEEP REFS UPDATED
========================================================= */
useEffect(() => {
  TT_1903_Ref.current = processData.TT_1903 ?? TT_1903_Ref.current;
}, [processData.TT_1903]);

useEffect(() => {
  TT_1904_Ref.current = processData.TT_1904 ?? TT_1904_Ref.current;
}, [processData.TT_1904]);

useEffect(() => {
  TT_1905_Ref.current = processData.TT_1905 ?? TT_1905_Ref.current;
}, [processData.TT_1905]);

useEffect(() => {
  TT_1906_Ref.current = processData.TT_1906 ?? TT_1906_Ref.current;
}, [processData.TT_1906]);

useEffect(() => {
  TT_1907_Ref.current = processData.TT_1907 ?? TT_1907_Ref.current;
}, [processData.TT_1907]);

useEffect(() => {
  TT_19010_Ref.current = processData.TT_19010 ?? TT_19010_Ref.current;
}, [processData.TT_19010]);

/* =========================================================
   E-101 HEAT EXCHANGER
========================================================= */
useEffect(() => {

  if (E101_isMounted.current) return;

  E101_isMounted.current = true;

  E101_intervalRef.current = setInterval(() => {

    const {
      TT_1901,
      TT_1902,
      xv1901Open,
      xv1902Open,
      xv1903Open,
      xv1907Open,
      xv1908Open,
      P101_Running,
      FIC_1902,
      FIC_1903
    } = processRef.current;

    const hotInlet = TT_1902;
    const crudeInlet = TT_1901;

    const E101A_HotFlow =
      xv1903Open && xv1908Open;

    const E101B_HotFlow =
      xv1903Open && xv1907Open;

    const E101A_ColdFlow =
      P101_Running && xv1901Open;

    const E101B_ColdFlow =
      P101_Running && xv1902Open;

    /* ==========================================
       HOT BRANCH TEMPERATURES
    ========================================== */

    const targetTT1906 =
      E101A_HotFlow ? hotInlet : 25;

    const targetTT1907 =
      E101B_HotFlow ? hotInlet : 25;

    TT_1906_Ref.current +=
      (targetTT1906 - TT_1906_Ref.current) * 0.10;

    TT_1907_Ref.current +=
      (targetTT1907 - TT_1907_Ref.current) * 0.10;

    /* ==========================================
       EXCHANGER A
    ========================================== */

    let targetTT1903 = crudeInlet;
    let E101A_HotOutlet = hotInlet;

    if (
      E101A_HotFlow &&
      E101A_ColdFlow
    ) {

      const deltaT =
        Math.max(
          0,
          hotInlet - crudeInlet
        );

      const crudeGain =
        Math.min(
          50,
          deltaT * 0.50
        );

      targetTT1903 =
        crudeInlet + crudeGain;

      E101A_HotOutlet =
        hotInlet - 28;
    }

    /* ==========================================
       EXCHANGER B
    ========================================== */

    let targetTT1904 = crudeInlet;
    let E101B_HotOutlet = hotInlet;

    if (
      E101B_HotFlow &&
      E101B_ColdFlow
    ) {

      const deltaT =
        Math.max(
          0,
          hotInlet - crudeInlet
        );

      const crudeGain =
        Math.min(
          50,
          deltaT * 0.50
        );

      targetTT1904 =
        crudeInlet + crudeGain;

      E101B_HotOutlet =
        hotInlet - 28;
    }

    TT_1903_Ref.current +=
      (targetTT1903 - TT_1903_Ref.current) * 0.08;

    TT_1904_Ref.current +=
      (targetTT1904 - TT_1904_Ref.current) * 0.08;

    /* ==========================================
       HOT SIDE MIXING
    ========================================== */

    const hotOutlets = [];

    if (E101A_HotFlow)
      hotOutlets.push(E101A_HotOutlet);

    if (E101B_HotFlow)
      hotOutlets.push(E101B_HotOutlet);

    let targetTT1905 = hotInlet;

    if (hotOutlets.length > 0) {

      targetTT1905 =
        hotOutlets.reduce(
          (a, b) => a + b,
          0
        ) / hotOutlets.length;
    }

    TT_1905_Ref.current +=
      (targetTT1905 - TT_1905_Ref.current) * 0.08;

    /* ==========================================
       COLD SIDE MIXING
    ========================================== */

    const totalFlow =
      FIC_1902 + FIC_1903;

    let targetTT19010 =
      crudeInlet;

    if (totalFlow > 0) {

      targetTT19010 =
        (
          TT_1903_Ref.current * FIC_1902 +
          TT_1904_Ref.current * FIC_1903
        ) / totalFlow;
    }

    TT_19010_Ref.current +=
      (targetTT19010 - TT_19010_Ref.current) * 0.08;

    /* ==========================================
       SINGLE CONTEXT UPDATE
    ========================================== */

    setProcessData(prev => ({
      ...prev,

      TT_1903: Number(TT_1903_Ref.current.toFixed(2)),
      TT_1904: Number(TT_1904_Ref.current.toFixed(2)),
      TT_1905: Number(TT_1905_Ref.current.toFixed(2)),
      TT_1906: Number(TT_1906_Ref.current.toFixed(2)),
      TT_1907: Number(TT_1907_Ref.current.toFixed(2)),
      TT_19010: Number(TT_19010_Ref.current.toFixed(2))
    }));

  }, 1000);

  return () => {

    if (E101_intervalRef.current) {

      clearInterval(
        E101_intervalRef.current
      );

      E101_intervalRef.current = null;
    }

    E101_isMounted.current = false;

  };

}, []);





/* =========================================================
   E-102A / E-102B HEAT EXCHANGER
========================================================= */

const TT_19012 = processData.TT_19012 ?? 80;
const TT_19013 = processData.TT_19013 ?? 45;


/* =========================================================
   REFS
========================================================= */

const E102_intervalRef = useRef(null);
const E102_isMounted = useRef(false);

const E102_processRef = useRef({});


/* =========================================================
   KEEP LIVE VALUES UPDATED
========================================================= */

useEffect(() => {

  E102_processRef.current = {

    TT_19010,
    TT_19011,
    xv1906Open,
    P101_Running

  };

});


/* =========================================================
   E-102 HEAT EXCHANGER
========================================================= */

useEffect(() => {

  if (E102_isMounted.current) return;

  E102_isMounted.current = true;

  console.log("✅ E-102 interval created");

  E102_intervalRef.current = setInterval(() => {

    const {

      TT_19010,
      TT_19011,
      xv1906Open,
      P101_Running

    } = E102_processRef.current;


    /* =====================================
       INLETS
    ===================================== */

    const keroseneFlow = xv1906Open;
    const crudeFlow = P101_Running;

    const stream1 =
      keroseneFlow
        ? Number(TT_19011) || 25
        : 25;

    const stream2 =
      crudeFlow
        ? Number(TT_19010) || 25
        : 25;


    let targetTT19012 = stream1;
    let targetTT19013 = stream2;


    /* =====================================
       E-102B
    ===================================== */

    let hotAfterB = stream1;
    let coldAfterB = stream2;

    if (
      keroseneFlow &&
      crudeFlow
    ) {

      const hotter =
        Math.max(stream1, stream2);

      const colder =
        Math.min(stream1, stream2);

      const deltaT =
        hotter - colder;

      if (deltaT > 0.05) {

        const effectiveness = 0.18;

        const duty = Math.max(
          0.20,
          deltaT * effectiveness
        );

        const secondaryDuty =
          duty * 0.28;

        if (stream1 >= stream2) {

          hotAfterB =
            stream1 - duty;

          coldAfterB =
            stream2 + secondaryDuty;

        }

        else {

          hotAfterB =
            stream1 + secondaryDuty;

          coldAfterB =
            stream2 - duty;

        }

      }

    }


    /* =====================================
       E-102A
    ===================================== */

    const hotter2 =
      Math.max(hotAfterB, coldAfterB);

    const colder2 =
      Math.min(hotAfterB, coldAfterB);

    const deltaT2 =
      hotter2 - colder2;

    if (
      keroseneFlow &&
      crudeFlow &&
      deltaT2 > 0.05
    ) {

      const effectiveness = 0.18;

      const duty = Math.max(
        0.20,
        deltaT2 * effectiveness
      );

      const secondaryDuty =
        duty * 0.28;

      if (hotAfterB >= coldAfterB) {

        targetTT19012 =
          hotAfterB - duty;

        targetTT19013 =
          coldAfterB + secondaryDuty;

      }

      else {

        targetTT19012 =
          hotAfterB + secondaryDuty;

        targetTT19013 =
          coldAfterB - duty;

      }

    }

    else {

      targetTT19012 = hotAfterB;
      targetTT19013 = coldAfterB;

    }


    /* =====================================
       PHYSICAL LIMITS
    ===================================== */

    if (targetTT19012 > targetTT19013) {

      targetTT19012 =
        Math.max(
          targetTT19012,
          targetTT19013 + 1
        );

    }

    else {

      targetTT19013 =
        Math.max(
          targetTT19013,
          targetTT19012 + 1
        );

    }


    /* =====================================
       THERMAL LAG
    ===================================== */

    setProcessData(prev => ({

      ...prev,

      TT_19012: Number(

        (

          (prev.TT_19012 ?? stream1)

          +

          (

            targetTT19012 -

            (prev.TT_19012 ?? stream1)

          ) * 0.08

        ).toFixed(2)

      ),

      TT_19013: Number(

        (

          (prev.TT_19013 ?? stream2)

          +

          (

            targetTT19013 -

            (prev.TT_19013 ?? stream2)

          ) * 0.08

        ).toFixed(2)

      )

    }));

  }, 1000);


  return () => {

    console.log("❌ E-102 interval cleared");

    if (E102_intervalRef.current) {

      clearInterval(E102_intervalRef.current);

      E102_intervalRef.current = null;

    }

    E102_isMounted.current = false;

  };

}, []);

  


/* =========================================================
   COOLING WATER INLET
========================================================= */

const TT_19014 =
  xv1909Open
    ? 32.234
    : 0;


/* =========================================================
   E-115 KEROSENE / WATER COOLER
========================================================= */

const TT_19015 =
  processData.TT_19015 ?? 40;

const TT_19016 =
  processData.TT_19016 ?? 30;


/* =========================================================
   REFS
========================================================= */

const E115_intervalRef =
  useRef(null);

const E115_isMounted =
  useRef(false);

const E115_processRef =
  useRef({});


/* =========================================================
   KEEP LIVE VALUES UPDATED
========================================================= */

useEffect(() => {

  E115_processRef.current = {

    TT_19012,
    TT_19014,
    xv1909Open

  };

});


/* =========================================================
   E-115 COOLER
========================================================= */

useEffect(() => {

  if (E115_isMounted.current) return;

  E115_isMounted.current = true;

  console.log("✅ E-115 interval created");

  E115_intervalRef.current = setInterval(() => {

    const {

      TT_19012,
      TT_19014,
      xv1909Open

    } = E115_processRef.current;


    /* =====================================
       INLETS
    ===================================== */

    const waterFlow = xv1909Open;

    const stream1 =
      Number(TT_19012) || 25;

    const stream2 =
      waterFlow
        ? Number(TT_19014) || 25
        : 25;


    let targetTT19015 = stream1;
    let targetTT19016 = stream2;


    /* =====================================
       DETERMINE HOT/COLD STREAM
    ===================================== */

    const hotter =
      Math.max(stream1, stream2);

    const colder =
      Math.min(stream1, stream2);

    const deltaT =
      hotter - colder;


    /* =====================================
       HEAT EXCHANGE
    ===================================== */

    if (
      waterFlow &&
      deltaT > 0.05
    ) {

      /*
         Cooler effectiveness
      */

      const effectiveness = 0.42;

      const duty = Math.max(
        0.20,
        deltaT * effectiveness
      );

      /*
         Cooling water receives
         less heat than kerosene loses
      */

      const secondaryDuty =
        duty * 0.36;

      if (stream1 >= stream2) {

        // Kerosene hotter

        targetTT19015 =
          stream1 - duty;

        targetTT19016 =
          stream2 + secondaryDuty;

      }

      else {

        // Water hotter

        targetTT19015 =
          stream1 + secondaryDuty;

        targetTT19016 =
          stream2 - duty;

      }

    }


    /* =====================================
       PHYSICAL LIMITS
    ===================================== */

    if (stream1 >= stream2) {

      targetTT19015 =
        Math.max(
          targetTT19015,
          stream2 + 1
        );

      targetTT19016 =
        Math.min(
          targetTT19016,
          stream1 - 1
        );

    }

    else {

      targetTT19016 =
        Math.max(
          targetTT19016,
          stream1 + 1
        );

      targetTT19015 =
        Math.min(
          targetTT19015,
          stream2 - 1
        );

    }


    /* =====================================
       THERMAL LAG
    ===================================== */

    setProcessData(prev => ({

      ...prev,

      TT_19015: Number(

        (

          (prev.TT_19015 ?? stream1)

          +

          (

            targetTT19015 -

            (prev.TT_19015 ?? stream1)

          ) * 0.08

        ).toFixed(2)

      ),

      TT_19016: Number(

        (

          (prev.TT_19016 ?? stream2)

          +

          (

            targetTT19016 -

            (prev.TT_19016 ?? stream2)

          ) * 0.08

        ).toFixed(2)

      )

    }));

  }, 1000);


  return () => {

    console.log("❌ E-115 interval cleared");

    if (E115_intervalRef.current) {

      clearInterval(E115_intervalRef.current);

      E115_intervalRef.current = null;

    }

    E115_isMounted.current = false;

  };

}, []);



const FIC_1905 = FIC_24013;
const PT_1905 = PT_24013 * 0.34;

const TT_19051 = xv19010Open ? TT_19015 : 25;
const PT_19051 = xv19010Open ? PT_1905 * 0.74 : 0.03;

const TT_19052 = xv1904Open ? TT_19015 : 25;
const PT_19052 = xv1904Open ? PT_1905 * 0.74 : 0.03;

const TT_19053 = xv1905Open ? TT_19015 : 25;
const PT_19053 = xv1905Open ? PT_1905 * 0.74 : 0.03;


  
/* =========================================================
              SPLITTING MAIN KEROSENE LINE
========================================================= */

const openKeroseneTankSplit =
[
  xv19010Open,
  xv1904Open,
  xv1905Open
].filter(Boolean).length;

const FIC_19051 =
  xv19010Open
    ? FIC_1905 / openKeroseneTankSplit
    : 0;

const FIC_19052 =
  xv1904Open
    ? FIC_1905 / openKeroseneTankSplit
    : 0;

const FIC_19053 =
  xv1905Open
    ? FIC_1905 / openKeroseneTankSplit
    : 0;









  // ✅ Update PlantStore without causing re-renders
  useEffect(() => {
    const current = PlantStore.get().transmitters;
    if (
      current.TT_19013 === TT_19013 &&
      current.PT_1901 === PT_1901 &&
      current.FIC_1901 === FIC_1901 &&
      current.FIC_19051 === FIC_19051 &&
      current.FIC_19052 === FIC_19052 &&
      current.FIC_19053 === FIC_19053 &&
      current.TT_19051 === TT_19051 &&
      current.TT_19052 === TT_19052 &&
      current.TT_19053 === TT_19053 &&
      current.PT_19051 === PT_19051 &&
      current.PT_19052 === PT_19052 &&
      current.PT_19053 === PT_19053
    ) {
      return;
    }

    PlantStore.set({
      transmitters: {
        ...current,
        TT_19013, FIC_19051, FIC_19052, FIC_19053, TT_19051, TT_19052, TT_19053, PT_19051, PT_19052, PT_19053,
        PT_1901,
        FIC_1901
      }
    });
  }, [TT_19013, PT_1901, FIC_1901, FIC_19051, FIC_19052, FIC_19053, TT_19051, TT_19052, TT_19053, PT_19051, PT_19052, PT_19053,]);

  return (
    <div className="PreHeatTrain1-container">
      {/* TITLE */}
      <h2 style={{
        position: "absolute",
        top: "0px",
        left: "240px",
        color: "white",
        background: "rgb(30, 5, 250)",
        padding: "2px 373px",
        zIndex: 10,
      }}>
        PRE-HEAT TRAIN 1
      </h2>

      {/* =================SHUTDOWN VALVES ================= */}
      <Valve
        className="XV-1901"
        tag="XV_1901"
        x={777}
        y={592}
        isClosed={valves.XV_1901}
        toggle={toggleValve}
        r={270}
      />

      <Valve
        className="XV-1902"
        tag="XV_1902"
        x={776}
        y={507}
        isClosed={valves.XV_1902}
        toggle={toggleValve}
        r={270}
      />

      <Valve
        className="XV-1903"
        tag="XV_1903"
        x={250}
        y={340}
        isClosed={valves.XV_1903}
        toggle={toggleValve}
      />

      <Valve
        className="XV-1904"
        tag="XV_1904"
        x={1180}
        y={449}
        isClosed={valves.XV_1904}
        toggle={toggleValve}
      />

      <Valve
        className="XV-1905"
        tag="XV_1905"
        x={1139}
        y={487}
        isClosed={valves.XV_1905}
        toggle={toggleValve}
        r={270}
      />

      <Valve
        className="XV-1906"
        tag="XV_1906"
        x={255}
        y={66}
        isClosed={valves.XV_1906}
        toggle={toggleValve}
      />

      <Valve
        className="XV-1907"
        tag="XV_1907"
        x={425}
        y={340}
        isClosed={valves.XV_1907}
        toggle={toggleValve}
      />

      <Valve
        className="XV-1908"
        tag="XV_1908"
        x={335}
        y={509}
        isClosed={valves.XV_1908}
        toggle={toggleValve}
      />

      <Valve
        className="XV-1909"
        tag="XV_1909"
        x={1085}
        y={291}
        isClosed={valves.XV_1909}
        toggle={toggleValve}
      />

      <Valve
        className="XV-19010"
        tag="XV_19010"
        x={1185}
        y={374}
        isClosed={valves.XV_19010}
        toggle={toggleValve}
      />

      {/* ================= CONTROL VALVE FACEPLATES ================= */}
      {openPanels.map((tag, index) => (
        <ControlValveControlPanel
          key={tag}
          tag={tag}
          data={valveData?.[tag] ?? DEFAULT_VALVES?.[tag]}
          update={updateValveData}
          close={() => closePanel(tag)}
          defaultPosition={{
            x: 300 + index * 40,
            y: 150 + index * 40
          }}
        />
      ))}

      {/* ================= PUMPS CONTROL VALVE FACEPLATES ================= */}
      {openPumpPanels.map((tag, index) => (
        <PumpFaceplate
          key={tag}
          tag={tag}
          close={() => closePumpPanel(tag)}
          defaultPosition={{
            x: 300 + index * 40,
            y: 150 + index * 40
          }}
        />
      ))}

      {/* ================= TRANSMITTERS ================= */}
      <Transmitter
        tag="FIC_1901"
        value={[FIC_1901]}
        x={835}
        y={747}
        unit="BPM"
        max={35}
        HH={34}
        H={30}
        L={0}
        LL={0}
        c="lime"
        description="FLOWRATE OF PRODUCTION CRUDE FROM DAY TANK (V-1502) VIA CRUDE PRODUCTION TRANSFER PUMPS TO PREHEAT TRAIN 1 HEAT EXCHANGER (E-101A/B)"
      />

      <Transmitter
        tag="TT_1901"
        value={[TT_1901]}
        x={585}
        y={757}
        unit="°C"
        max={70}
        HH={56}
        H={45}
        L={0}
        LL={0}
        c="lime"
        description="TEMPERATURE OF PRODUCTION CRUDE FROM DAY TANK (V-1502) VIA CRUDE PRODUCTION TRANSFER PUMPS TO PREHEAT TRAIN 1 HEAT EXCHANGER (E-101A/B)"
      />

      <Transmitter
        tag="PT_1901"
        value={[PT_1901]}
        x={475}
        y={787}
        unit="MPa"
        max={3}
        HH={2.5}
        H={1.8}
        L={0}
        LL={0}
        c="lime"
        description="PRESSURE OF PRODUCTION CRUDE FROM DAY TANK (V-1502) VIA CRUDE PRODUCTION TRANSFER PUMPS TO PREHEAT TRAIN 1 HEAT EXCHANGER (E-101A/B)"
      />

      <Transmitter
        tag="PT_1905"
        value={[PT_1905]}
        x={982}
        y={248}
        unit="MPa"
        max={3}
        HH={2.5}
        H={1.8}
        L={0}
        LL={0}
        c="lime"
        description="PRESSURE OF KEROSENE ALL THE WAY FROM DIESEL STRIPPER THROUGH P-105A/B TO E-115"
      />

      <Transmitter
        tag="FIC_1902"
        value={[FIC_1902]}
        x={702}
        y={659}
        unit="BPM"
        max={35}
        HH={34}
        H={30}
        L={0}
        LL={0}
        c="lime"
        description="FLOWRATE OF PRODUCTION CRUDE FROM CRUDE PRODUCTION TRANSFER PUMPS TO INLET OF HEAT EXCHANGER (E-101A)"
      />

      <Transmitter
        tag="FIC_1903"
        value={[FIC_1903]}
        x={719}
        y={449}
        unit="BPM"
        max={35}
        HH={34}
        H={30}
        L={0}
        LL={0}
        c="lime"
        description="FLOWRATE OF PRODUCTION CRUDE FROM CRUDE PRODUCTION TRANSFER PUMPS TO INLET OF HEAT EXCHANGER (E-101B)"
      />

      <Transmitter
        tag="FIC_1904"
        value={[FIC_1904]}
        x={499}
        y={299}
        unit="BPM"
        max={35}
        HH={34}
        H={30}
        L={0}
        LL={0}
        c="lime"
        description="FLOWRATE OF PRODUCTION CRUDE FROM CRUDE PRODUCTION TRANSFER PUMPS TO INLET OF HEAT EXCHANGER (E-102A)"
      />

      <Transmitter
        tag="FIC_1905"
        value={[FIC_1905]}
        x={782}
        y={248}
        unit="BPM"
        max={35}
        HH={34}
        H={30}
        L={0}
        LL={0}
        c="lime"
        description="FLOWRATE OF KEROSENE ALL THE WAY FROM DIESEL STRIPPER THROUGH P-105A/B TO E-115"
      />

      <Transmitter
        tag="TT_1902"
        value={[TT_1902]}
        x={135}
        y={325}
        unit="°C"
        max={200}
        HH={156}
        H={145}
        L={0}
        LL={0}
        c="lime"
        description="TEMPERATURE OF OVERHEAD NAPHTHA VAPOR PRODUCTION FROM T-102"
      />

      <Transmitter
        tag="TT_1903"
        value={[TT_1903]}
        x={312}
        y={385}
        unit="°C"
        max={200}
        HH={156}
        H={145}
        L={0}
        LL={0}
        c="lime"
        description="TEMPERATURE OF CRUDE FROM P-101A/B TO E-101A TUBE SIDE INLET"
      />

      <Transmitter
        tag="TT_1906"
        value={[TT_1906]}
        x={215}
        y={505}
        unit="°C"
        max={200}
        HH={156}
        H={145}
        L={0}
        LL={0}
        c="lime"
        description="TEMPERATURE OF OVERHEAD NAPHTHA VAPOR PRODUCTION FROM T-102 TO E-101A SHELL SIDE INLET"
      />

      <Transmitter
        tag="TT_1907"
        value={[TT_1907]}
        x={526}
        y={355}
        unit="°C"
        max={200}
        HH={156}
        H={145}
        L={0}
        LL={0}
        c="lime"
        description="TEMPERATURE OF OVERHEAD NAPHTHA VAPOR PRODUCTION FROM T-102 TO E-101B SHELL SIDE INLET"
      />

      <Transmitter
        tag="TT_1904"
        value={[TT_1904]}
        x={685}
        y={335}
        unit="°C"
        max={200}
        HH={156}
        H={145}
        L={0}
        LL={0}
        c="lime"
        description="TEMPERATURE OF CRUDE FROM P-101A/B TO E-101B TUBE SIDE INLET"
      />

      <Transmitter
        tag="TT_1905"
        value={[TT_1905]}
        x={1035}
        y={659}
        unit="°C"
        max={200}
        HH={156}
        H={145}
        L={0}
        LL={0}
        c="lime"
        description="TEMPERATURE OF OVERHEAD NAPHTHA VAPOR PRODUCTION FROM T-102 TO A-102A-D NAPHTHA COOLERS"
      />

      <Transmitter
        tag="TT_19010"
        value={[TT_19010]}
        x={402}
        y={297}
        unit="°C"
        max={130}
        HH={116}
        H={105}
        L={0}
        LL={0}
        c="lime"
        description="TEMPERATURE OF CRUDE GOING TO E-102A"
      />

      <Transmitter
        tag="TT_19011"
        value={[TT_19011]}
        x={132}
        y={101}
        unit="°C"
        max={200}
        HH={186}
        H={165}
        L={0}
        LL={0}
        c="lime"
        description="TEMPERATURE OF KEROSENE FROM E-106A/B TO E-102A"
      />

      <Transmitter
        tag="TT_19012"
        value={[TT_19012]}
        x={882}
        y={248}
        unit="°C"
        max={200}
        HH={186}
        H={165}
        L={0}
        LL={0}
        c="lime"
        description="TEMPERATURE OF KEROSENE FROM E-102A/B TO E-115 COOLER"
      />

      <Transmitter
        tag="TT_19013"
        value={[TT_19013]}
        x={1132}
        y={188}
        unit="°C"
        max={200}
        HH={186}
        H={165}
        L={0}
        LL={0}
        c="lime"
        description="TEMPERATURE OF KEROSENE FROM E-102A/B TO E-103A/B IN PRE-HEAT TRAIN 2"
      />

      <Transmitter
        tag="TT_19014"
        value={[TT_19014]}
        x={1142}
        y={278}
        unit="°C"
        max={200}
        HH={186}
        H={165}
        L={0}
        LL={0}
        c="lime"
        description="TEMPERATURE OF FRESH WATER SUPPLY FROM FRESH WATER MANIFOLD TO E-115 COOLER TUBE SIDE INLET"
      />

      <Transmitter
        tag="TT_19015"
        value={[TT_19015]}
        x={962}
        y={483}
        unit="°C"
        max={200}
        HH={186}
        H={165}
        L={0}
        LL={0}
        c="lime"
        description="TEMPERATURE OF KEROSENE FROM E-115 COOLER TO KEROSENE STORAGE TANK OR UNQUIALIFIED MANIFOLD"
      />

      <Transmitter
        tag="TT_19016"
        value={[TT_19016]}
        x={1102}
        y={568}
        unit="°C"
        max={200}
        HH={186}
        H={165}
        L={0}
        LL={0}
        c="lime"
        description="TEMPERATURE OF FRESH WATER RETURN FROM E-115 COOLER"
      />

      <Pump
        tag="P-101A"
        x={389}
        y={749}
        rpm={1800}
        bladeCount={pumpData?.["P-101A"]?.bladeCount || 3}
        size={pumpData?.["P-101A"]?.size || 43}
        onClick={openPumpPanel}
      />

      <Pump
        tag="P-101B"
        x={389}
        y={835}
        rpm={1800}
        bladeCount={pumpData?.["P-101B"]?.bladeCount || 3}
        size={pumpData?.["P-101B"]?.size || 43}
        onClick={openPumpPanel}
      />
    </div>
  );
}

export default memo(PreHeatTrain1);